import { ClientError, GraphQLClient } from 'graphql-request';

import { getDemoTenantGraphqlMockResponse } from 'src/common/services/api/demoTenantGraphqlMocks';
import { getAccessToken, isLoggedIn, logout } from 'src/common/services/AuthenticationService';
import { Logger } from 'src/common/services/Logger';
import { getUserTenant } from 'src/common/services/TenantsService';

import { RoutesHelper } from '../../helpers/RoutesHelper';

const GRAPHQL_ENDPOINT = `${process.env.API_ENDPOINT}/graphql`;

let demoTenantStatus: boolean | null = null;
let demoTenantStatusPromise: Promise<boolean> | null = null;

const isDemoTenant = async (): Promise<boolean> => {
	if (demoTenantStatus !== null) {
		return demoTenantStatus;
	}

	if (!demoTenantStatusPromise) {
		demoTenantStatusPromise = (async () => {
			if (!isLoggedIn()) {
				return false;
			}

			const tenant = await getUserTenant();
			// eslint-disable-next-line no-console
			console.log('tenant', tenant);
			return tenant?.name === 'Evinced Dev Team'; // DEMO_TENANT_NAME;
		})().catch(() => false);
	}

	demoTenantStatus = await demoTenantStatusPromise;
	return demoTenantStatus;
};

const extractOperationName = (query?: string, operationName?: string): string | undefined => {
	if (operationName) {
		return operationName;
	}

	if (!query) {
		return undefined;
	}

	const match = query.match(/\b(query|mutation)\s+([A-Za-z0-9_]+)/);
	return match?.[2];
};

const maybeMockGraphqlResponse = async (
	body: BodyInit | null | undefined
): Promise<Response | null> => {
	if (typeof body !== 'string') {
		return null;
	}

	let parsedBody: { operationName?: string; query?: string; variables?: Record<string, unknown> };
	try {
		parsedBody = JSON.parse(body);
	} catch {
		return null;
	}

	const operationName = extractOperationName(parsedBody.query, parsedBody.operationName);
	if (!operationName) {
		return null;
	}

	const isDemo = await isDemoTenant();
	if (!isDemo) {
		return null;
	}

	Logger.info('Attempting to mock GraphQL request', { operationName });

	const mockResponse = getDemoTenantGraphqlMockResponse(operationName, parsedBody.variables);
	if (!mockResponse) {
		Logger.warn('No mock found for operation', { operationName });
		return null;
	}

	Logger.info('Mock response created', { operationName });

	return new Response(JSON.stringify(mockResponse), {
		status: 200,
		headers: {
			'Content-Type': 'application/json'
		}
	});
};

export const gqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
	headers: () => ({
		Authorization: `Bearer ${getAccessToken()}`,
		'Content-Type': 'application/graphql'
	}),
	fetch: async (input, init) => {
		const mockedResponse = await maybeMockGraphqlResponse(init?.body);
		if (mockedResponse) {
			return mockedResponse;
		}

		return fetch(input, init);
	},
	responseMiddleware: async (response) => {
		if (response instanceof ClientError) {
			const error = response;

			Logger.error('Error from server', error);
			if (error.response?.status === 401) {
				await logout();
			} else if (error.response?.status === 403) {
				// redirect occurs. flow ends here
				window.location.href = RoutesHelper.getUnauthorized403Page();
			}
		}
	}
});
